import { useMemo, useRef, useState } from 'react';
import { aiJobsBackendConfigured } from '../../backend/aiJobsClient';
import { newAiJobId } from '../../domain/aiJob';
import type { V2Area, V2ChecklistItem, V2Inspection } from '../../domain/inspection';
import { useT } from '../../i18n/i18n';
import { aiFailureMessage } from '../../i18n/labels';
import { submitAdvancedReviewAction } from '../../state/aiJobsStore';
import { aiPolicyBlockFor } from '../../state/aiStore';
import { useStore } from '../../state/createStore';
import { evidenceForIds, evidenceStore } from '../../state/evidenceStore';
import { requestPushAfterAdvancedSubmit } from '../../state/pushRegistration';
import { sessionStore } from '../../state/sessionStore';
import { Button, InlineMessage, SectionEyebrow, SheetShell } from '../../ui/components';
import { useOnlineStatus } from '../../ui/hooks';
import { Thumbnail } from '../evidence/PhotoStrip';

// 高级 AI 深度复核 — the start sheet (Final Owner Decision 2026-09-14 §11.3): what will be analyzed
// (the row's linked photos, the guidance, the AI observations, the inspector note), 查看 / 调整照片
// (back to ② — never a second picker), the frozen cost / background lines, the frozen risk line, and
// 开始深度复核. The job id is minted when the sheet opens, so a double tap is ONE job (§18).
export function AdvancedReviewSheet(props: { inspection: V2Inspection; area: V2Area; item: V2ChecklistItem; onClose: () => void; onAdjustPhotos: () => void }) {
  const { t, tp } = useT();
  const online = useOnlineStatus();
  const signedIn = useStore(sessionStore, (s) => s.phase === 'signedIn');
  const evidenceState = useStore(evidenceStore, (s) => s);
  const photos = useMemo(() => evidenceForIds(evidenceState, props.item.evidenceIds), [evidenceState, props.item.evidenceIds]);
  const [jobId] = useState(() => newAiJobId());
  const [busy, setBusy] = useState(false);
  const starting = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const policyBlock = aiPolicyBlockFor(props.inspection);
  const configured = aiJobsBackendConfigured();
  const canStart = !busy && online && signedIn && configured && policyBlock === null && photos.length > 0;

  async function start() {
    if (busy || starting.current) return;
    starting.current = true;
    setBusy(true);
    setError(null);
    try {
      const outcome = await submitAdvancedReviewAction({ inspectionId: props.inspection.id, areaId: props.area.id, itemId: props.item.id, jobId });
      if (outcome.ok) {
        // §40: the notification permission is explained (once) only now, after the first real use.
        void requestPushAfterAdvancedSubmit();
        props.onClose();
        return;
      }
      setError(outcome.code === 'offline' ? t('ai.needsOnline') : aiFailureMessage(t, outcome.code));
    } finally {
      starting.current = false;
      setBusy(false);
    }
  }

  return (
    <SheetShell
      full
      title={t('advanced.title')}
      onClose={props.onClose}
      layer="over"
      testId="advanced-review-sheet"
      footer={
        <>
          <Button icon="psychology" onClick={() => void start()} disabled={!canStart} testId="advanced-start">
            {busy ? t('advanced.starting') : t('advanced.start')}
          </Button>
          <Button variant="text" onClick={props.onClose} disabled={busy} testId="advanced-cancel">
            {t('shell.cancel')}
          </Button>
        </>
      }
    >
      <p className="card-body" data-testid="advanced-will-analyze">
        {tp('advanced.willAnalyze', photos.length)}
      </p>
      <ul className="subtle-list" data-testid="advanced-inputs">
        <li>{t('advanced.inputGuidance')}</li>
        <li>{t('advanced.inputObservations')}</li>
        <li>{t('advanced.inputNote')}</li>
      </ul>
      <p className="card-body">{t('advanced.deeper')}</p>
      <section className="ai-detail-section">
        <SectionEyebrow>{t('advanced.photosLabel')}</SectionEyebrow>
        {photos.length === 0 ? <p className="card-note">{t('advanced.noPhotos')}</p> : null}
        {photos.length > 0 ? (
          <div className="photo-row" data-testid="advanced-photos">
            {photos.map((ev) => (
              <Thumbnail key={ev.id} evidence={ev} onClick={() => undefined} />
            ))}
          </div>
        ) : null}
        <Button
          variant="secondary"
          size="small"
          block={false}
          icon="perm_media"
          onClick={() => {
            props.onClose();
            props.onAdjustPhotos();
          }}
          testId="advanced-adjust-photos"
        >
          {t('advanced.adjustPhotos')}
        </Button>
      </section>
      {/* The explanation stands in plain sight before the button can be reached (§11.3). */}
      <p className="advanced-explainer" data-testid="advanced-explainer">
        {t('advanced.explainer')}
      </p>
      <p className="ai-disclaimer" data-testid="advanced-disclaimer">
        {t('ai.disclaimer')}
      </p>
      {!online ? <InlineMessage kind="info" text={t('ai.needsOnline')} /> : null}
      {policyBlock !== null ? <InlineMessage kind="info" text={aiFailureMessage(t, policyBlock)} /> : null}
      {error ? <InlineMessage kind="err" text={error} /> : null}
    </SheetShell>
  );
}
